/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GUILD_MESSAGE, NOTIFICATION_MESSAGE, PROFILE_MESSAGE } from 'i18n/constants'
import { notification } from 'antd'

import ModalManagerMember from 'features/Guild/components/ModalManagerMember'
import { RequestLoginModal, RaceTable, SwitchBtn } from 'features/Race/components'
import { useAppDispatch, useAppSelector, useDebounce, useToggle, useUpdateEffect } from 'hooks'
import {
  Career,
  GetRaceListParams,
  RaceClassNumber,
  RaceFieldType,
  RaceStatus,
  RegionFilters,
  RegionOption,
  RegionOptionFilter
} from 'models'
import { SearchInput } from 'shared'
import { STRING, NUMBER, STRING_REGION } from 'apps/constants'
import { guildCurrentRaceColumns, guildHistoryRaceColumns } from 'utils/columns'
import { links } from 'apps'
import constants from 'apps/constants'
import guildApi from 'apis/guildApi'
import { handleAsyncRequest, shortenUserName } from 'utils/helper'
import GuildRaceStyled from './styled'
import Button from 'shared/Button'
import TabContainer from 'features/Profile/components/TabsUserCreateRace/styled'
import { LINE_BOT } from 'assets/images'
import ModalUserCreateRace from 'features/Profile/components/ModalUserCreateRace'
import Select from 'shared/Select'
import { ClassFilters, RaceClassFilter, RaceTypeFilter, TypeFilters } from 'features/Race/pages/Open'
import { saveDataInput } from 'apps/redux/userCreateRace/userCreateRace.slice'

const GUILD_RACE_TABS = {
  CURRENT: 'current',
  HISTORY: 'history'
}

const defaultParams: GetRaceListParams = {
  limit: NUMBER.TWENTY,
  page: NUMBER.ONE,
  status: RaceStatus.OPEN
}

const defaultTypeFilters: TypeFilters = [
  {
    name: 'All',
    isActive: true
  },
  {
    name: RaceFieldType.Turf,
    isActive: false
  },
  {
    name: RaceFieldType.Dirt,
    isActive: false
  }
]
const defaultClassFilters: ClassFilters = [
  {
    name: 'All',
    isActive: true
  },
  {
    name: RaceClassNumber.Class1,
    isActive: false
  },
  {
    name: RaceClassNumber.Class2,
    isActive: false
  },
  {
    name: RaceClassNumber.Class3,
    isActive: false
  },
  {
    name: RaceClassNumber.Class4,
    isActive: false
  },
  {
    name: RaceClassNumber.Class5,
    isActive: false
  },
  {
    name: RaceClassNumber.Class6,
    isActive: false
  },
  {
    name: RaceClassNumber.Class7,
    isActive: false
  },
  {
    name: RaceClassNumber.ClassFreeStyle,
    isActive: false
  }
]
const defaultRegionFilters: RegionFilters = [
  {
    name: 'All',
    isActive: true
  },
  {
    name: RegionOption.TOKYO,
    isActive: false
  },
  {
    name: RegionOption.SEOUL,
    isActive: false
  }
]

export default function GuildRace() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [api, contextHolder] = notification.useNotification()
  const [searchParams, setSearchParams] = useSearchParams()
  const { profile } = useAppSelector(state => state)
  const [params, setParams] = useState<GetRaceListParams>(defaultParams)
  const [classFilters, setClassFilters] = useState<ClassFilters>(defaultClassFilters)
  const [typeFilters, setTypeFilters] = useState<TypeFilters>(defaultTypeFilters)
  const [regionFilters, setRegionFilters] = useState<RegionFilters>(defaultRegionFilters)
  const paramsSearch: any = Object.fromEntries(searchParams.entries())
  const [classSelect, setClassSelect] = useState(paramsSearch?.raceClass || 'Class')
  const [typeSelect, setTypeSelect] = useState(paramsSearch?.fieldType || 'Field type')
  const [regionSelect, setRegionSelect] = useState(paramsSearch?.region || 'Region')
  const [searchValue, setSearchValue] = useState<string>('')
  const debounceSearchValue = useDebounce<string>(searchValue, constants.DEBOUNCE_TIME)
  const [isRequestLoginModalOpen, toggleIsRequestLoginModalOpen] = useToggle(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [guildRace, setGuildRace] = useState<any>([])
  const [total, setTotal] = useState()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const userIdRef = useRef<any>(null)
  const [tabActive, setTabActive] = useState<any>(t(GUILD_RACE_TABS.CURRENT))
  const [isModalGuildRaceCreate, setIsModalGuildRaceCreate] = useToggle(false)
  const [isFreeOn, toggleIsFreeOn] = useToggle(JSON.parse(paramsSearch?.freeRace || 'false'))
  const [isMyHorseOn, toggleIsMyHorseOn] = useToggle(JSON.parse(paramsSearch?.myHorse || 'false'))
  const useRefSwitch = useRef<HTMLButtonElement>(null)
  const auth = useAppSelector(state => state.auth)
  const [isShowAction, setIsShowAction] = useState(false)
  const dispatch = useAppDispatch()

  const memoizedResultListSelectionColumns = useMemo(() => {
    return tabActive === GUILD_RACE_TABS.CURRENT ? guildCurrentRaceColumns : guildHistoryRaceColumns
  }, [profile, tabActive])

  useEffect(() => {
    dispatch(saveDataInput({}))
  }, [])

  useEffect(() => {
    if (profile?.role_in_guild === 'MASTER') {
      setIsShowAction(true)
    }
  }, [profile])

  useUpdateEffect(() => {
    setGuildRace([])
    setParams({ ...params, freeRace: isFreeOn, page: NUMBER.ONE })
  }, [isFreeOn])

  useUpdateEffect(() => {
    setGuildRace([])
    setParams({ ...params, myHorse: isMyHorseOn, page: NUMBER.ONE })
  }, [isMyHorseOn])

  useEffect(() => {
    if (profile.guild_id !== NUMBER.ZERO) {
      setSearchParams(params as any)
      getListGuildRace()
    }
  }, [params, profile])

  const getListGuildRace = async () => {
    setLoading(true)
    const [error, result]: any = await handleAsyncRequest(guildApi.getListGuildRace(profile.guild_id, { ...params }))

    if (error) {
      openNotification(STRING.ERROR, error.message || error.errors[0]?.message)
    }

    if (result) {
      const formatData = result.data.records.map((record: any) => {
        return {
          ...record,
          member: shortenUserName(record.user_name),
          winRate: handleCareer(record.career),
          raceName: record.name
        }
      })
      setGuildRace(params.page > NUMBER.ONE ? [...guildRace, ...formatData] : formatData)
      setTotal(result.data?.total || NUMBER.ZERO)
    }

    setLoading(false)
  }

  useUpdateEffect(() => {
    setParams({ ...params, page: NUMBER.ONE, search: debounceSearchValue })
  }, [debounceSearchValue])

  const handleSearchValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }

  const _onTabClick = (tab: string) => {
    setGuildRace([])
    setTabActive(tab)
    const status = tab === GUILD_RACE_TABS.CURRENT ? RaceStatus.OPEN : RaceStatus.CLOSED
    setParams({ ...params, page: NUMBER.ONE, search: debounceSearchValue, status })
  }

  const onClickApproveMember = (value: any) => {
    userIdRef.current = value?.id
    setShowModal(!showModal)
  }

  const handleRowItemClick = (value: any) => {
    navigate(links.race.detail(value))
  }

  const openNotification = (message: string, description: string) => {
    notification.config({
      duration: NUMBER.FOUR
    })

    api.info({
      message,
      description,
      placement: 'bottomRight',
      className: message === STRING.ERROR ? 'ant-custom-error' : 'ant-custom-success'
    })
  }

  const handleCareer = (career: Career) => {
    const totalRace = career?.total_number_of_races
    const firstClass = career?.first_count
    const secondClass = career?.second_count
    const thirdClass = career?.third_count
    const tltClass = (firstClass + secondClass + thirdClass) / totalRace
    const newTltClass = isNaN(tltClass) ? 0 : tltClass * 100
    const winRateResult = `${
      Number.isInteger((firstClass / totalRace) * 100)
        ? (firstClass / totalRace) * 100
        : ((firstClass / totalRace) * 100).toFixed(2)
    }% - ${Number.isInteger(newTltClass) ? newTltClass : newTltClass.toFixed(2)}%`
    let valueDisplay = ''
    if (totalRace === 0) {
      valueDisplay = '---'
    }
    if (totalRace > 0 && firstClass === 0 && secondClass === 0 && thirdClass === 0) {
      valueDisplay = '0.00% - 0.00%'
    }
    if (totalRace !== 0 && (firstClass !== 0 || secondClass !== 0 || thirdClass !== 0)) {
      valueDisplay = winRateResult
    }
    return valueDisplay
  }

  const _handleToggleModal = () => {
    setShowModal(!showModal)
  }

  const _handleUpgrade = async () => {
    setIsLoading(true)
    const [error, result]: any = await handleAsyncRequest(guildApi.putAppove(profile.guild_id, userIdRef.current))
    setIsLoading(false)
    if (error) {
      openNotification(STRING.ERROR, error.message || error.errors[NUMBER.ZERO]?.message)
    }

    if (result) {
      getListGuildRace()
      _handleToggleModal()
      openNotification(STRING.SUCCESS, t(`${GUILD_MESSAGE}.message.approve_success`))
    }
  }

  const handleClickTab = (value: string) => () => {
    if (value === tabActive) return
    setTabActive(value)
    _onTabClick(value)
  }

  const handleConfirmClick = () => {
    setParams({
      ...params,
      page: NUMBER.ONE
    })
  }

  const handleCloseGuildRaceCreate = () => {
    setIsModalGuildRaceCreate(!isModalGuildRaceCreate)
  }

  const updateClassFilters = useCallback((classFilters: ClassFilters, clickedFilter: RaceClassFilter) => {
    const clonedClassFilters = [...classFilters]

    clonedClassFilters.forEach(filter => {
      if (filter.name === clickedFilter.name) {
        filter.isActive = true
        setClassSelect(filter.name)
      } else {
        filter.isActive = false
      }
    })

    return clonedClassFilters
  }, [])

  const updateParamsAfterFilterClicked = useCallback((params: GetRaceListParams, clickedFilter: RaceClassFilter) => {
    const clonedParams = { ...params, page: 1 }

    if (clickedFilter.name !== 'All') {
      clonedParams.raceClass = clickedFilter.name
    } else {
      delete clonedParams.raceClass
    }

    return clonedParams
  }, [])

  const updateTypeFilters = useCallback((typeFilters: TypeFilters, clickedFilter: RaceTypeFilter) => {
    const clonedTypeFilters = [...typeFilters]

    clonedTypeFilters.forEach(filter => {
      if (filter.name === clickedFilter.name) {
        filter.isActive = true
        setTypeSelect(filter.name)
      } else {
        filter.isActive = false
      }
    })

    return clonedTypeFilters
  }, [])

  const updateRegionFilters = useCallback((regionFilters: RegionFilters, clickedFilter: RegionOptionFilter) => {
    const clonedRegionFilters = [...regionFilters]

    clonedRegionFilters.forEach(filter => {
      if (filter.name === clickedFilter.name) {
        filter.isActive = true
        setRegionSelect(filter.name)
      } else {
        filter.isActive = false
      }
    })

    return clonedRegionFilters
  }, [])

  const updateParamsAfterTypeClicked = useCallback((params: GetRaceListParams, clickedFilter: RaceTypeFilter) => {
    const clonedParams = { ...params, page: 1 }

    if (clickedFilter.name !== 'All') {
      clonedParams.fieldType = clickedFilter.name
    } else {
      delete clonedParams.fieldType
    }

    return clonedParams
  }, [])

  const updateParamsAfterRegionClicked = useCallback((params: GetRaceListParams, clickedFilter: RegionOptionFilter) => {
    const clonedParams = { ...params, page: 1 }

    if (clickedFilter.name !== 'All') {
      if (clickedFilter.name === STRING_REGION.Tokyo) {
        clonedParams.region = STRING_REGION.TOKYO
      } else {
        clonedParams.region = STRING_REGION.SEOUL
      }
    } else {
      delete clonedParams.region
    }

    return clonedParams
  }, [])

  const handleClassFilterClicked = useCallback(
    (filterName: string) => () => {
      const clickedFilter = classFilters.find(filter => filter.name === filterName)

      if (!clickedFilter) return

      const newClassFilters = updateClassFilters(classFilters, clickedFilter)
      const newParams = updateParamsAfterFilterClicked(params, clickedFilter)
      setGuildRace([])
      setClassFilters(newClassFilters)
      setParams(newParams)
    },
    [classFilters, params]
  )

  const handleTypeFilterClicked = useCallback(
    (filterName: string) => () => {
      const clickedFilter = typeFilters.find(filter => filter.name === filterName)

      if (!clickedFilter) return

      const newTypeFilters = updateTypeFilters(typeFilters, clickedFilter)
      const newParams = updateParamsAfterTypeClicked(params, clickedFilter)

      setGuildRace([])
      setTypeFilters(newTypeFilters)
      setParams(newParams)
    },
    [classFilters, params]
  )

  const handleRegionFilterClicked = useCallback(
    (filterName: string) => () => {
      const clickedFilter = regionFilters.find(filter => filter.name === filterName)

      if (!clickedFilter) return

      const newRegionFilters = updateRegionFilters(regionFilters, clickedFilter)
      const newParams = updateParamsAfterRegionClicked(params, clickedFilter)

      setGuildRace([])
      setRegionFilters(newRegionFilters)
      setParams(newParams)
    },
    [regionFilters, params]
  )

  return (
    <GuildRaceStyled>
      {contextHolder}
      {isShowAction && (
        <div className='button-box'>
          <Button
            buttonName={t(`${PROFILE_MESSAGE}.btnCreateRace`)}
            onClickButton={handleCloseGuildRaceCreate}
            type='btnCreateRace'
            height={52}
            width={181}
          />
        </div>
      )}
      <div className='search-box'>
        <SearchInput
          searchValue={searchValue}
          handleSearchValueChanged={handleSearchValueChanged}
          customClass='search-horse-input'
          placeholder={t(`${GUILD_MESSAGE}.search`)}
        />
        <div className='d-flex flex-wrap align-items-center gap-4 dropdown'>
          <Select
            dataSelect={regionFilters}
            nameSelect={regionSelect}
            onSelected={(filterName: string) => handleRegionFilterClicked(filterName)()}
          />
          <Select
            dataSelect={classFilters}
            nameSelect={classSelect}
            onSelected={(filterName: string) => handleClassFilterClicked(filterName)()}
          />
          <Select
            dataSelect={typeFilters}
            nameSelect={typeSelect}
            onSelected={(filterName: string) => handleTypeFilterClicked(filterName)()}
          />
        </div>
        <SwitchBtn title='FREE' isOn={isFreeOn} handleSwitchBtnClicked={toggleIsFreeOn} />
        {auth.isLogged && (
          <SwitchBtn
            title={t(`${NOTIFICATION_MESSAGE}.myHorse`)}
            isOn={isMyHorseOn}
            handleSwitchBtnClicked={toggleIsMyHorseOn}
            refSwitch={useRefSwitch}
          />
        )}
      </div>
      <div className='box-tabs-current-race'>
        <TabContainer>
          <div className='tabs-contain'>
            <span
              onClick={handleClickTab(t(GUILD_RACE_TABS.CURRENT))}
              className={`color-white title margin-rigth-tab ${
                tabActive === t(GUILD_RACE_TABS.CURRENT) ? 'active' : ''
              }`}
            >
              {t(`${GUILD_MESSAGE}.${GUILD_RACE_TABS.CURRENT}`)}
              {tabActive === t(GUILD_RACE_TABS.CURRENT) ? <img src={LINE_BOT} className='active' alt='' /> : null}
            </span>
            <span
              onClick={handleClickTab(t(GUILD_RACE_TABS.HISTORY))}
              className={`color-white title ${tabActive === t(GUILD_RACE_TABS.HISTORY) ? 'active' : ''}`}
            >
              {t(`${GUILD_MESSAGE}.${GUILD_RACE_TABS.HISTORY}`)}
              {tabActive === t(GUILD_RACE_TABS.HISTORY) ? <img src={LINE_BOT} className='active' alt='' /> : null}
            </span>
          </div>
        </TabContainer>
      </div>
      <div className='members'>
        <RaceTable
          columns={memoizedResultListSelectionColumns}
          data={guildRace ?? []}
          raisePage={setParams}
          loader={!loading}
          params={params}
          isRowClickable
          checkLoadingPage={false}
          totalRace={total}
          currentUser={profile}
          noDataText={t(`${NOTIFICATION_MESSAGE}.dataNotFound`)}
          onClickOneCell={id => onClickApproveMember(id)}
          onRowClick={handleRowItemClick}
          isShowVipIcon={true}
        />
      </div>

      {showModal && (
        <ModalManagerMember
          title={t(`${GUILD_MESSAGE}.title_modal_approve`)}
          message={
            <p>
              {t(`${GUILD_MESSAGE}.content_modal_approve`)}
              <br />
              {t(`${GUILD_MESSAGE}.content_modal_approve2`)}
            </p>
          }
          onClose={_handleToggleModal}
          onConfirm={_handleUpgrade}
          loading={isLoading}
          titleColor='rgb(78, 240, 118)'
          width={515}
          heigth={240}
        />
      )}

      {isRequestLoginModalOpen && <RequestLoginModal toggleIsModalOpen={toggleIsRequestLoginModalOpen} />}

      {isModalGuildRaceCreate && (
        <ModalUserCreateRace
          onConfirmCreateRaceClick={handleConfirmClick}
          toggleIsModalOpen={setIsModalGuildRaceCreate}
          onCloseButtonClick={handleCloseGuildRaceCreate}
          isGuildRace
        />
      )}
    </GuildRaceStyled>
  )
}
